import React, { useState, useEffect } from 'react';
import { Table, Button } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';

const ITEMS_PER_PAGE = 25;

const AssessmentsTable = ({ initialAssessments, hasArchivedDocs }) => {
  const [assessments, setAssessments] = useState(initialAssessments || []);
  const [showArchivedLink, setShowArchivedLink] = useState(hasArchivedDocs);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  // Initialize current page from URL parameter (0-based for react-paginate)
  const getInitialPage = () => {
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get('page'), 10);
    return page > 0 ? page - 1 : 0;
  };

  const [currentPage, setCurrentPage] = useState(getInitialPage());
  const onArchivesPage = window.location.search.includes('archive=1');

  const docCount = assessments.length;

  useEffect(() => {
    // Reset to page 0 if current page is now out of range
    const totalPages = Math.ceil(assessments.length / ITEMS_PER_PAGE);
    if (currentPage >= totalPages && totalPages > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentPage(totalPages - 1);
    }
  }, [assessments.length, currentPage]);

  const handleArchiveToggle = async (assessment) => {
    try {
      const response = await fetch(`/assessments/${assessment.id}.json`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({
          archive: assessment.archive ? '0' : '1'
        })
      });

      if (response.ok) {
        await response.json();

        // Remove the assessment from the list with fade effect
        setAssessments(prev => prev.filter(a => a.id !== assessment.id));

        // Show archived link if we just archived something
        if (!assessment.archive) {
          setShowArchivedLink(true);
        }

        // If on archives page and count is 0, redirect
        if (docCount - 1 === 0 && onArchivesPage) {
          window.location.assign('/assessments');
        }
      }
    } catch (error) {
      console.error('Error toggling archive:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const getArchiveButtonText = (assessment) => {
    return assessment.archive ? 'Unarchive' : 'Archive';
  };

  const getCompanyLogoUrl = (domain) => {
    const token = window.gon?.logo_dev_publishable_key;
    return `https://img.logo.dev/${domain}?token=${token}`;
  };

  const getScoreColor = (score) => {
    if (score == null || score === '-') return '#6b7280'; // Gray for no score

    const numScore = parseFloat(score);

    // Color gradient from red (0) to green (1000)
    if (numScore >= 900) {
      return '#22c55e'; // Bright green for high scores
    } else if (numScore >= 750) {
      return '#84cc16'; // Yellow-green
    } else if (numScore >= 600) {
      return '#eab308'; // Yellow
    } else if (numScore >= 400) {
      return '#f97316'; // Orange
    } else {
      return '#dc2626'; // Red for low scores
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
    // Reset to first page when sorting
    setCurrentPage(0);
  };

  const getFilteredAssessments = () => {
    if (!searchTerm.trim()) return assessments;

    return assessments.filter(assessment =>
      assessment.url.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getSortedAssessments = () => {
    const filtered = getFilteredAssessments();
    if (!sortColumn) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'date':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'url':
          aValue = a.url.toLowerCase();
          bValue = b.url.toLowerCase();
          break;
        case 'company':
          aValue = a.company?.name?.toLowerCase() || '';
          bValue = b.company?.name?.toLowerCase() || '';
          break;
        case 'score':
          // Handle null/undefined scores by treating them as -1
          aValue = a.score != null ? parseFloat(a.score) : -1;
          bValue = b.score != null ? parseFloat(b.score) : -1;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <span className="ms-1 text-muted">↕</span>;
    }
    return sortDirection === 'asc' ?
      <span className="ms-1">↑</span> :
      <span className="ms-1">↓</span>;
  };

  if (assessments.length === 0) {
    return (
      <div>
        Assessments will be listed here once you submit an URL for evaluation.
      </div>
    );
  }

  // Check if filtered results are empty
  const sortedAssessmentsForCheck = getSortedAssessments();
  const hasNoFilteredResults = searchTerm && sortedAssessmentsForCheck.length === 0;

  const getArchiveLink = () => {
    if (onArchivesPage) {
      return <a href="/assessments">View your pages</a>;
    } else if (showArchivedLink) {
      return <a href="/assessments?archive=1">View your archived pages</a>;
    }
    return null;
  };

  // Pagination logic
  const sortedAssessments = getSortedAssessments();
  const filteredCount = sortedAssessments.length;
  const totalPages = Math.ceil(sortedAssessments.length / ITEMS_PER_PAGE);
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentAssessments = sortedAssessments.slice(startIndex, endIndex);

  const handlePageClick = (event) => {
    const newPage = event.selected;
    setCurrentPage(newPage);

    // Update URL with page parameter (1-based for URL)
    const url = new URL(window.location);
    if (newPage === 0) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', newPage + 1);
    }
    window.history.pushState({}, '', url);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 mt-4" style={{ padding: '0 25px' }}>
        <h2 className="mb-0">
          Your Assessments ({docCount} document{docCount !== 1 ? 's' : ''})
        </h2>

        <div className="position-relative" style={{ width: '400px' }}>
          <span className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
            </svg>
          </span>
          <input
            id="assessment-search"
            type="text"
            className="form-control ps-5"
            placeholder="Search Assessments"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(0); // Reset to first page when searching
            }}
            style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}
          />
        </div>
      </div>

      <div id="summary">
        {(showArchivedLink || onArchivesPage) && (
          <div id="archived-link">
            {getArchiveLink()}
          </div>
        )}
      </div>

      {hasNoFilteredResults ? (
        <div className="alert alert-info">
          No assessments found matching &quot;{searchTerm}&quot;. Try a different search term.
        </div>
      ) : (
        <Table hover responsive className="assessments-table">
          <thead>
            <tr>
              <th className="date" onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                Date{getSortIcon('date')}
              </th>
              <th className="company" onClick={() => handleSort('company')} style={{ cursor: 'pointer', width: '200px' }}>
                Company{getSortIcon('company')}
              </th>
              <th className="url" onClick={() => handleSort('url')} style={{ cursor: 'pointer', width: '400px' }}>
                URL{getSortIcon('url')}
              </th>
              <th className="score" onClick={() => handleSort('score')} style={{ cursor: 'pointer' }}>
                Score{getSortIcon('score')}
              </th>
              <th className="status">Status</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentAssessments.map(doc => (
              <tr key={doc.id} id={`yula-${doc.id}`}>
                <td className="date align-middle">{formatDate(doc.created_at)}</td>
                <td className="company align-middle">
                  {doc.company ? (
                    <div className="d-flex flex-column align-items-center">
                      <span className="fw-bold mb-2">{doc.company.name}</span>
                      <img
                        src={getCompanyLogoUrl(doc.company.domain)}
                        alt={`${doc.company.name} logo`}
                        style={{ maxWidth: '75px', maxHeight: '75px' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td className="url align-middle">
                  <a href={doc.url} target="_blank" rel="noopener noreferrer">
                    {doc.url}
                  </a>
                </td>
                <td className="score align-middle text-center">
                  <a
                    href={`/assessments/${doc.id}`}
                    style={{ color: getScoreColor(doc.score) }}
                  >
                    {doc.score || '-'}
                  </a>
                </td>
                <td className="status align-middle">{doc.status}</td>
                <td className="archive align-middle text-center">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleArchiveToggle(doc)}
                    className="js-toggle-archive-button"
                  >
                    {getArchiveButtonText(doc)}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {!hasNoFilteredResults && filteredCount > 0 && (
        <div className="text-center text-muted small mt-3 mb-2">
          Showing {startIndex + 1} to {Math.min(endIndex, filteredCount)} of {filteredCount} assessments
        </div>
      )}

      {!hasNoFilteredResults && totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={handlePageClick}
            pageRangeDisplayed={3}
            marginPagesDisplayed={2}
            pageCount={totalPages}
            previousLabel="< Previous"
            renderOnZeroPageCount={null}
            forcePage={currentPage}
            containerClassName="pagination"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakClassName="page-item"
            breakLinkClassName="page-link"
            activeClassName="active"
            disabledClassName="disabled"
          />
        </div>
      )}
    </div>
  );
};

export default AssessmentsTable;
